/**
 * Welcome Step - Company info, SIRET, Sector
 * Light theme, conversational "Je suis Max" tone
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Building2, Sparkles, Hash, Briefcase } from 'lucide-react';

// Pas de libellé ici : constante de module figée au chargement (piège n°1 i18n).
// Résolu au rendu via onboarding.welcome.sectors.<value>.
const SECTORS = [
  { value: 'btp' },
  { value: 'transport' },
  { value: 'services' },
  { value: 'commerce' },
  { value: 'industrie' },
  { value: 'autre' },
];

export function WelcomeStep({ data, updateData, onNext, onSkip }) {
  const { t } = useTranslation();
  const handleSubmit = (e) => {
    e.preventDefault();
    onNext();
  };

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Greeting */}
      <div className="text-center space-y-4">
        <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-amber-400 to-amber-600 rounded-2xl shadow-lg shadow-amber-500/20">
          <Sparkles className="w-10 h-10 text-white" />
        </div>
        <h2 className="text-3xl font-bold text-gray-900">
          {t('onboarding.welcome.greeting')}
        </h2>
        <p className="text-lg text-gray-500 max-w-md mx-auto">
          {t('onboarding.welcome.intro')}
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Company name */}
        <div className="space-y-2">
          <label className="text-gray-700 text-sm font-medium">
            {t('onboarding.welcome.companyNameLabel')}
          </label>
          <div className="relative">
            <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={data.companyName}
              onChange={(e) => updateData({ companyName: e.target.value })}
              placeholder={t('onboarding.welcome.companyNamePlaceholder')}
              className="w-full pl-12 pr-4 py-4 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all text-lg"
              autoFocus
            />
          </div>
        </div>

        {/* SIRET + Sector row */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-gray-700 text-sm font-medium flex items-center gap-1.5">
              <Hash className="w-3.5 h-3.5" />
              {t('onboarding.welcome.siretLabel')}
            </label>
            <input
              type="text"
              value={data.siret}
              onChange={(e) => {
                const v = e.target.value.replace(/[^0-9]/g, '').slice(0, 14);
                updateData({ siret: v });
              }}
              placeholder="123 456 789 00012"
              maxLength={17}
              className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all font-mono"
            />
          </div>
          <div className="space-y-2">
            <label className="text-gray-700 text-sm font-medium flex items-center gap-1.5">
              <Briefcase className="w-3.5 h-3.5" />
              {t('onboarding.welcome.sectorLabel')}
            </label>
            <select
              value={data.sector}
              onChange={(e) => updateData({ sector: e.target.value })}
              className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
            >
              <option value="">{t('common.actions.selectPlaceholder')}</option>
              {SECTORS.map(s => (
                <option key={s.value} value={s.value}>{t(`onboarding.welcome.sectors.${s.value}`)}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Address (optional) */}
        <div className="space-y-4 pt-4 border-t border-gray-200">
          <p className="text-gray-400 text-sm">
            {t('onboarding.welcome.addressSectionLabel')}
          </p>

          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <input
                type="text"
                value={data.companyAddress}
                onChange={(e) => updateData({ companyAddress: e.target.value })}
                placeholder={t('onboarding.welcome.addressPlaceholder')}
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
              />
            </div>
            <input
              type="text"
              value={data.companyPostalCode}
              onChange={(e) => updateData({ companyPostalCode: e.target.value })}
              placeholder={t('onboarding.welcome.postalCodePlaceholder')}
              className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
            />
            <input
              type="text"
              value={data.companyCity}
              onChange={(e) => updateData({ companyCity: e.target.value })}
              placeholder={t('onboarding.welcome.cityPlaceholder')}
              className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
            />
          </div>
        </div>

        {/* Skip hint */}
        <div className="pt-4 text-center">
          <button
            type="button"
            onClick={onSkip}
            className="text-gray-400 hover:text-gray-600 text-sm underline transition-colors"
          >
            {t('onboarding.welcome.skipHint')}
          </button>
        </div>
      </form>
    </div>
  );
}

export default WelcomeStep;
